import * as React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { FilterSelect, FilterSelectItem } from './filter-select.tsx';

export type PaginatedTableProps<T> = {
  /** Client-side pagination: all rows. */
  data?: T[];
  /** Server-side pagination: loads one page. */
  loadData?: (page: number, pageSize: number) => Promise<{ data: T[]; total: number }>;
  /** Values that trigger a reload (server-side) and a reset to page 1. */
  dependencies?: unknown[];
  renderTable: (rows: T[], isLoading: boolean) => React.ReactNode;
  externalLoading?: boolean;
  /** After the first load, refetches swap rows in without the loading state (no blink). */
  quietRefetch?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  wrapInCard?: boolean;
  className?: string;
};

const NAV_BUTTON =
  'flex items-center justify-center size-6 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed';

/** Adds pagination (client- or server-side) around any table renderer. */
export function PaginatedTable<T>({
  data,
  loadData,
  dependencies = [],
  renderTable,
  externalLoading = false,
  quietRefetch = false,
  defaultPageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  wrapInCard = false,
  className = '',
}: PaginatedTableProps<T>) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  const [internalData, setInternalData] = React.useState<T[]>([]);
  const [total, setTotal] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const hasLoadedOnce = React.useRef(false);
  const loadRef = React.useRef(loadData);
  loadRef.current = loadData;

  const isServerSide = typeof loadData === 'function';
  const totalPages = Math.ceil(total / pageSize) || 1;
  const depsKey = JSON.stringify(dependencies);

  React.useEffect(() => {
    if (!isServerSide) return;
    let cancelled = false;
    const showSpinner = !(quietRefetch && hasLoadedOnce.current);
    (async () => {
      if (showSpinner) setIsLoading(true);
      try {
        const result = await loadRef.current!(page, pageSize);
        if (cancelled) return;
        setInternalData(result.data || []);
        setTotal(result.total || 0);
        hasLoadedOnce.current = true;
      } catch {
        if (!cancelled && !quietRefetch) {
          setInternalData([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled && showSpinner) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, depsKey, isServerSide, quietRefetch]);

  React.useEffect(() => {
    if (dependencies.length > 0) setPage(1);
  }, [depsKey]);

  React.useEffect(() => {
    if (!isServerSide && data) {
      setTotal(data.length);
      const start = (page - 1) * pageSize;
      setInternalData(data.slice(start, start + pageSize));
    }
  }, [data, page, pageSize, isServerSide]);

  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) setPage(1);
  }, [page, totalPages]);

  const loading = isLoading || externalLoading;

  const paginationControls = total > 0 && (
    <div className="flex flex-col gap-3 mt-5 items-stretch md:flex-row md:flex-wrap md:items-center md:justify-between">
      <div className="flex items-center gap-2 justify-end md:justify-start">
        <FilterSelect
          value={String(pageSize)}
          onValueChange={(v) => {
            setPageSize(parseInt(v, 10));
            setPage(1);
          }}
          className="h-6 w-[148px] px-2"
        >
          {pageSizeOptions.map((size) => (
            <FilterSelectItem key={size} value={String(size)}>
              {size} per page
            </FilterSelectItem>
          ))}
        </FilterSelect>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1 justify-center md:justify-start">
          <button
            aria-label="First page"
            onClick={() => setPage(1)}
            disabled={page === 1}
            className={NAV_BUTTON}
          >
            <ChevronsLeft size={14} />
          </button>
          <button
            aria-label="Previous page"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className={NAV_BUTTON}
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-sm font-normal px-2 text-page-text">
            {page} of {totalPages} pages
          </span>
          <button
            aria-label="Next page"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className={NAV_BUTTON}
          >
            <ChevronRight size={14} />
          </button>
          <button
            aria-label="Last page"
            onClick={() => setPage(totalPages)}
            disabled={page >= totalPages}
            className={NAV_BUTTON}
          >
            <ChevronsRight size={14} />
          </button>
        </div>
      )}
    </div>
  );

  const content = (
    <div className={className}>
      {renderTable(internalData, loading)}
      {paginationControls}
    </div>
  );

  return wrapInCard ? <div className="bg-card-bg rounded-[5px] overflow-hidden">{content}</div> : content;
}
